import type { ITerminalOptions, ITheme } from "@xterm/xterm";

export const terminalThemes: Record<string, ITheme> = {
  default: {
    background: "#111111",
    foreground: "#dddddd",
    cursor: "#e6e6e6",
    selectionBackground: "#35506b",
  },
  iterm2: {
    background: "#000000",
    foreground: "#c7c7c7",
    cursor: "#c7c7c7",
    selectionBackground: "#3f638b",
    black: "#000000",
    red: "#c91b00",
    green: "#00c200",
    yellow: "#c7c400",
    blue: "#0225c7",
    magenta: "#c930c7",
    cyan: "#00c5c7",
    white: "#c7c7c7",
    brightBlack: "#676767",
    brightRed: "#ff6d67",
    brightGreen: "#5ff967",
    brightYellow: "#fefb67",
    brightBlue: "#6871ff",
    brightMagenta: "#ff76ff",
    brightCyan: "#5ffdff",
    brightWhite: "#feffff",
  },
  "solarized-dark": {
    background: "#002b36",
    foreground: "#839496",
    cursor: "#93a1a1",
    selectionBackground: "#073642",
    black: "#073642",
    red: "#dc322f",
    green: "#859900",
    yellow: "#b58900",
    blue: "#268bd2",
    magenta: "#d33682",
    cyan: "#2aa198",
    white: "#eee8d5",
    brightBlack: "#002b36",
    brightRed: "#cb4b16",
    brightGreen: "#586e75",
    brightYellow: "#657b83",
    brightBlue: "#839496",
    brightMagenta: "#6c71c4",
    brightCyan: "#93a1a1",
    brightWhite: "#fdf6e3",
  },
  "tango-dark": {
    background: "#000000",
    foreground: "#d3d7cf",
    cursor: "#d3d7cf",
    selectionBackground: "#204a87",
    black: "#2e3436",
    red: "#cc0000",
    green: "#4e9a06",
    yellow: "#c4a000",
    blue: "#3465a4",
    magenta: "#75507b",
    cyan: "#06989a",
    white: "#d3d7cf",
    brightBlack: "#555753",
    brightRed: "#ef2929",
    brightGreen: "#8ae234",
    brightYellow: "#fce94f",
    brightBlue: "#729fcf",
    brightMagenta: "#ad7fa8",
    brightCyan: "#34e2e2",
    brightWhite: "#eeeeec",
  },
  "solarized-light": {
    background: "#fdf6e3",
    foreground: "#52676f",
    cursor: "#586e75",
    cursorAccent: "#fdf6e3",
    selectionBackground: "#d8cda8",
    selectionInactiveBackground: "#e6dcc0",
    selectionForeground: "#263b43",
    black: "#073642",
    red: "#a92b29",
    green: "#596900",
    yellow: "#806000",
    blue: "#176b9f",
    magenta: "#a52a65",
    cyan: "#14736b",
    // ANSI white/bright colors are also used as text, not just backgrounds.
    white: "#586e75",
    brightBlack: "#5e737b",
    brightRed: "#ab3d11",
    brightGreen: "#526400",
    brightYellow: "#765900",
    brightBlue: "#1d628f",
    brightMagenta: "#6256a0",
    brightCyan: "#136c66",
    brightWhite: "#3b5058",
  },
  "paper-light": {
    background: "#faf7f2",
    foreground: "#2f3437",
    cursor: "#2f3437",
    cursorAccent: "#faf7f2",
    selectionBackground: "#dbe8ff",
    selectionInactiveBackground: "#e5e9f0",
    selectionForeground: "#1f2326",
    black: "#1f2326",
    red: "#c43c35",
    green: "#52752a",
    yellow: "#906500",
    blue: "#285cc7",
    magenta: "#8d3daf",
    cyan: "#007680",
    white: "#62605c",
    brightBlack: "#6b7277",
    brightRed: "#ba3b34",
    brightGreen: "#4f7026",
    brightYellow: "#886100",
    brightBlue: "#345eb4",
    brightMagenta: "#8a3da8",
    brightCyan: "#006d78",
    brightWhite: "#303438",
  },
};

export function terminalThemeOptions(
  theme: ITheme,
): Pick<ITerminalOptions, "theme" | "minimumContrastRatio"> {
  // Our palettes use opaque #rrggbb backgrounds. Adjust at render time as TUIs
  // can emit pale 256-color/RGB text that bypasses the theme's ANSI palette.
  const rgb = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(theme.background ?? "");
  const [r, g, b] = rgb
    ? rgb.slice(1).map((hex) => {
        const channel = parseInt(hex, 16) / 255;
        return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
      })
    : [0, 0, 0];
  const light = 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5;
  return { theme, minimumContrastRatio: light ? 4.5 : 1 };
}

const THEME_STORAGE_KEY = "chatcode.terminal.theme";
const THEME_CHANGE_EVENT = "chatcode:terminal-theme-change";

export function getStoredTerminalTheme(): string {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw && terminalThemes[raw]) return raw;
  } catch {
    /* ignore */
  }
  return "default";
}

export function storeTerminalTheme(name: string) {
  const previous = getStoredTerminalTheme();
  try {
    localStorage.setItem(THEME_STORAGE_KEY, name);
  } catch {
    /* ignore */
  }
  // Native storage events only reach other documents, not the Settings tab
  // that made this change. Notify mounted (including memoized/hidden) views.
  if (typeof window !== "undefined" && getStoredTerminalTheme() !== previous) {
    window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
  }
}

export function subscribeTerminalTheme(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY || event.key === null) onChange();
  };
  window.addEventListener(THEME_CHANGE_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
  };
}
